import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import {
  Campaign,
  CampaignApproval,
  CampaignApprovalStatus,
  OutletGroup,
  PlaybackErrorRow,
  VenuePartner,
} from '../../types';

/**
 * CampaignAnalyticsPanel — embeddable campaign analytics view, extracted
 * from the old standalone CampaignCoveragePage. Leads with stat tiles and
 * an approval-funnel bar (the "how is this campaign doing" headline),
 * then the per-venue approval table and playback-health table as
 * supporting detail below.
 *
 * Read-only by design — actions on an approval row (approve / reject /
 * revoke) live on AdApprovalsManagement which already handles role
 * scoping.
 */

const STATUS_PALETTE: Record<
  CampaignApprovalStatus,
  { bg: string; fg: string; label: string }
> = {
  PROPOSED: { bg: '#FFEDD5', fg: '#C2410C', label: 'PROPOSED' },
  APPROVED: { bg: '#DCFCE7', fg: '#166534', label: 'APPROVED' },
  REJECTED: { bg: '#FEE2E2', fg: '#B91C1C', label: 'REJECTED' },
  REVOKED: { bg: '#F3F4F6', fg: '#374151', label: 'REVOKED' },
};

// Fixed status→hue order for the funnel bar. Reuses the same reserved
// status colors as the table chips above so identity stays consistent
// between the headline visual and the detail rows.
const FUNNEL_ORDER: CampaignApprovalStatus[] = [
  'APPROVED',
  'PROPOSED',
  'REJECTED',
  'REVOKED',
];
const FUNNEL_COLOR: Record<CampaignApprovalStatus, string> = {
  APPROVED: '#16A34A',
  // Amber, not the brand pink secondary — this is a status color
  // (awaiting decision), same family as STATUS_PALETTE.PROPOSED.fg
  // below, and must stay visually distinct from primary/accent so it
  // never reads as a brand CTA.
  PROPOSED: '#EA580C',
  REJECTED: '#DC2626',
  REVOKED: '#9CA3AF',
};

interface CampaignAnalyticsPanelProps {
  campaign: Campaign;
}

const CampaignAnalyticsPanel: React.FC<CampaignAnalyticsPanelProps> = ({
  campaign,
}) => {
  const campaignId = campaign.id;

  const [approvals, setApprovals] = useState<CampaignApproval[]>([]);
  const [venues, setVenues] = useState<VenuePartner[]>([]);
  const [outletGroups, setOutletGroups] = useState<OutletGroup[]>([]);
  const [playbackErrors, setPlaybackErrors] = useState<PlaybackErrorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [approvalsRes, venuesRes, errorsRes] = await Promise.all([
        apiService.listCampaignApprovals({ campaign_id: campaignId, limit: 1000 }),
        apiService.listVenuePartners({ limit: 1000 }),
        apiService.getPlaybackErrors().catch(() => ({ data: [], from: '', to: '' })),
      ]);
      setApprovals(approvalsRes.data);
      setVenues(venuesRes.data);
      setPlaybackErrors(errorsRes.data ?? []);

      const venueIds = Array.from(new Set(approvalsRes.data.map((a) => a.venue_partner_id)));
      const groupBuckets = await Promise.all(
        venueIds.map((vId) =>
          apiService.listOutletGroupsForVenue(vId).catch(() => [] as OutletGroup[]),
        ),
      );
      setOutletGroups(groupBuckets.flat());
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    void load();
  }, [load]);

  const venueById = useMemo(() => {
    const m = new Map<string, VenuePartner>();
    venues.forEach((v) => m.set(v.id, v));
    return m;
  }, [venues]);

  const campaignErrors = useMemo(
    () =>
      playbackErrors
        .filter((r) => r.campaign_id === campaignId)
        .sort((a, b) => b.error_count - a.error_count),
    [playbackErrors, campaignId],
  );

  const counts = useMemo(() => {
    const m: Record<CampaignApprovalStatus, number> = {
      PROPOSED: 0,
      APPROVED: 0,
      REJECTED: 0,
      REVOKED: 0,
    };
    approvals.forEach((a) => {
      m[a.status] = (m[a.status] || 0) + 1;
    });
    return m;
  }, [approvals]);

  const outletGroupById = useMemo(() => {
    const m = new Map<string, OutletGroup>();
    outletGroups.forEach((g) => m.set(g.id, g));
    return m;
  }, [outletGroups]);

  const isLive = useCallback(
    (a: CampaignApproval) => {
      if (a.status !== 'APPROVED') return false;
      if (campaign.state !== 'PUBLISHED') return false;
      const now = Date.now();
      const start = campaign.published_time_start
        ? new Date(campaign.published_time_start).getTime()
        : -Infinity;
      const end = campaign.published_time_end
        ? new Date(campaign.published_time_end).getTime()
        : Infinity;
      return now >= start && now <= end;
    },
    [campaign],
  );

  const liveCount = useMemo(() => approvals.filter(isLive).length, [approvals, isLive]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const fmt = (s?: string) => (s ? new Date(s).toLocaleString() : '—');
  const funnelTotal = approvals.length;

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} variant="outlined" size="small">
          Refresh
        </Button>
      </Stack>

      {/* ---- headline: stat tiles ---- */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' },
          gap: 1.5,
          mb: 3,
        }}
      >
        <StatTile label="Deployed now" value={liveCount} color="#16A34A" sublabel="live on screens" />
        <StatTile label="Submitted to" value={approvals.length} sublabel={approvals.length === 1 ? 'venue' : 'venues'} />
        <StatTile label="Approved" value={counts.APPROVED} color={FUNNEL_COLOR.APPROVED} />
        <StatTile label="Proposed" value={counts.PROPOSED} color={FUNNEL_COLOR.PROPOSED} sublabel="awaiting venue" />
        <StatTile label="Incidents (30d)" value={campaignErrors.length} color={campaignErrors.length > 0 ? '#DC2626' : undefined} />
      </Box>

      {approvals.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          This campaign hasn't been submitted to any venues yet — switch to
          the Targeting tab to pick venues and outlet groups.
        </Alert>
      ) : (
        /* ---- approval funnel bar ---- */
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#94A3B8',
              mb: 1.5,
            }}
          >
            Approval funnel · {funnelTotal} venue{funnelTotal === 1 ? '' : 's'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              height: 16,
              borderRadius: 999,
              overflow: 'hidden',
              gap: '3px',
              bgcolor: '#E2E8F0',
            }}
          >
            {FUNNEL_ORDER.filter((s) => counts[s] > 0).map((s) => (
              <Tooltip key={s} title={`${STATUS_PALETTE[s].label} · ${counts[s]}`}>
                <Box
                  sx={{
                    width: `${(counts[s] / funnelTotal) * 100}%`,
                    bgcolor: FUNNEL_COLOR[s],
                    minWidth: 6,
                    borderRadius: 999,
                    transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              </Tooltip>
            ))}
          </Box>
          <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ mt: 1.75 }}>
            {FUNNEL_ORDER.filter((s) => counts[s] > 0).map((s) => (
              <Stack key={s} direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: FUNNEL_COLOR[s] }} />
                <Typography sx={{ fontSize: 12, color: '#64748B' }}>
                  {STATUS_PALETTE[s].label} <strong>{counts[s]}</strong>
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* ---- per-venue table (supporting detail) ---- */}
      {approvals.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 4, borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Venue</TableCell>
                <TableCell>Outlets targeted</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Requested at</TableCell>
                <TableCell>Decided at</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {approvals.map((a) => {
                const venue = venueById.get(a.venue_partner_id);
                const palette = STATUS_PALETTE[a.status];
                const reason =
                  a.status === 'REJECTED'
                    ? a.reject_reason
                    : a.status === 'REVOKED'
                      ? a.revoked_reason
                      : '';
                const group = a.outlet_group_id ? outletGroupById.get(a.outlet_group_id) : undefined;
                const live = isLive(a);
                return (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {venue?.display_name || venue?.legal_name || a.venue_partner_id}
                      </Typography>
                      {venue && (
                        <Typography variant="caption" color="text.secondary">
                          {venue.legal_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {a.outlet_group_id ? (
                        <>
                          <Typography variant="body2">
                            {group?.display_name || a.outlet_group_id}
                          </Typography>
                          {group?.kind && (
                            <Typography variant="caption" color="text.secondary">
                              {group.kind === 'ANY' ? 'all outlets' : group.kind === 'SYSTEM_AUTO' ? 'auto' : 'curated'}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          All outlets in venue
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Chip
                          size="small"
                          label={palette.label}
                          sx={{ bgcolor: palette.bg, color: palette.fg, fontWeight: 700 }}
                        />
                        {live && (
                          <Chip
                            size="small"
                            label="LIVE"
                            sx={{ bgcolor: '#15803D', color: 'white', fontWeight: 700, letterSpacing: 0.5 }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{fmt(a.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{fmt(a.decided_at || a.revoked_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 320, display: 'block' }}>
                        {reason || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ---- playback health ---- */}
      <Box>
        <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Playback health
          </Typography>
          <Typography variant="caption" color="text.secondary">
            last 30 days · {campaignErrors.length} {campaignErrors.length === 1 ? 'incident' : 'incidents'}
          </Typography>
        </Stack>
        {campaignErrors.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No playback errors reported for this campaign in the last 30
              days. Devices are rendering the assets without
              codec/network/DRM failures.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Device</TableCell>
                  <TableCell>Hardware</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell>Error code</TableCell>
                  <TableCell align="right">Failures</TableCell>
                  <TableCell>Last seen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaignErrors.map((row) => {
                  const hw = [row.manufacturer, row.model].filter(Boolean).join(' · ');
                  return (
                    <TableRow key={`${row.device_id}|${row.campaign_asset_id}|${row.error_code}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.device_name || row.device_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.device_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{hw || '—'}</Typography>
                        {row.device_type && (
                          <Chip size="small" label={row.device_type} sx={{ ml: 0.5, height: 18, fontSize: 10 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {row.campaign_asset_id.slice(0, 8)}…
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.error_code}
                          sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontFamily: 'monospace', fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}>
                          {row.error_count.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {fmt(row.last_seen)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

// ---- helpers --------------------------------------------------

const StatTile: React.FC<{
  label: string;
  value: number;
  color?: string;
  sublabel?: string;
}> = ({ label, value, color, sublabel }) => (
  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#94A3B8',
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 28,
        fontWeight: 800,
        color: color || '#0F172A',
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value.toLocaleString()}
    </Typography>
    {sublabel && (
      <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.5 }}>
        {sublabel}
      </Typography>
    )}
  </Paper>
);

export default CampaignAnalyticsPanel;
